<template>
  <div>
    <h3>用户管理</h3>
    <el-form :inline="true" size="small" class="filter-form">
      <el-form-item label="邮箱">
        <el-input v-model="filters.email" clearable placeholder="模糊搜索" @keyup.enter.native="onSearch" />
      </el-form-item>
      <el-form-item label="档位">
        <el-select v-model="filters.plan" clearable placeholder="全部">
          <el-option v-for="opt in planOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="filters.status" clearable placeholder="全部">
          <el-option label="正常" value="ACTIVE" />
          <el-option label="已禁用" value="DISABLED" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="onSearch">查询</el-button>
        <el-button @click="onReset">重置</el-button>
      </el-form-item>
    </el-form>

    <el-table
      v-loading="loading"
      :data="items"
      stripe
      :default-sort="{ prop: sortProp, order: sortOrder }"
      @sort-change="onSortChange"
    >
      <el-table-column prop="email" label="邮箱" min-width="180" sortable="custom" />
      <el-table-column label="档位" width="120" sortable="custom" prop="plan">
        <template slot-scope="{ row }">{{ planLabel(row.plan) }}</template>
      </el-table-column>
      <el-table-column label="验证" width="80">
        <template slot-scope="{ row }">
          <el-tag :type="row.emailVerified ? 'success' : 'warning'" size="mini">
            {{ row.emailVerified ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template slot-scope="{ row }">
          {{ row.status === 'ACTIVE' ? '正常' : '已禁用' }}
        </template>
      </el-table-column>
      <el-table-column label="注册时间" width="170" sortable="custom" prop="createdAt">
        <template slot-scope="{ row }">{{ formatDateTime(row.createdAt) }}</template>
      </el-table-column>
      <el-table-column label="会员开通" width="170" sortable="custom" prop="planStartedAt">
        <template slot-scope="{ row }">{{ formatDateTime(row.planStartedAt) }}</template>
      </el-table-column>
      <el-table-column label="会员到期" width="170" sortable="custom" prop="planExpiresAt">
        <template slot-scope="{ row }">{{ formatDateTime(row.planExpiresAt) }}</template>
      </el-table-column>
      <el-table-column prop="slicesToday" label="今日切片" width="90" />
      <el-table-column prop="slicesMonth" label="本月切片" width="90" />
      <el-table-column label="操作" width="100" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openEdit(row)">编辑</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      layout="total, prev, pager, next"
      :current-page="page + 1"
      :page-size="size"
      :total="total"
      @current-change="onPageChange"
    />

    <el-dialog title="编辑用户" :visible.sync="dialogVisible" width="520px">
      <el-form label-width="120px">
        <el-form-item label="邮箱"><el-input :value="editForm.email" disabled /></el-form-item>
        <el-form-item label="档位">
          <el-select v-model="editForm.plan">
            <el-option v-for="opt in planOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="editForm.status">
            <el-option label="正常" value="ACTIVE" />
            <el-option label="已禁用" value="DISABLED" />
          </el-select>
        </el-form-item>
        <el-form-item label="会员到期">
          <el-date-picker
            v-model="editForm.planExpiresAt"
            type="datetime"
            placeholder="留空表示不限期"
            value-format="yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
            clearable
          />
        </el-form-item>
        <el-form-item label="单文件 override">
          <el-input-number
            v-model="editForm.overrideMaxFileMb"
            :min="0"
            :max="testUploadMaxSizeMb"
            :step="256"
            :disabled="!testUploadEnabled"
            controls-position="right"
            placeholder="留空/0 表示按档位"
          />
          <span class="unit-suffix">MB</span>
          <div class="form-hint">
            <template v-if="testUploadEnabled">
              测试模式已开启，可为该账号单独放宽至 {{ testUploadMaxSizeMb }} MB（不对普通用户开放）
            </template>
            <template v-else>请先在「系统配置」开启测试上传硬顶</template>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="save">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { fetchUsers, updateUser, fetchAdminConfig } from '../api/admin';
import { planLabel, formatDateTime, USER_PLAN_LABELS } from '../constants/planLabels';

const PLAN_OPTIONS = Object.entries(USER_PLAN_LABELS).map(([value, label]) => ({ value, label }));

export default {
  name: 'UserList',
  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      page: 0,
      size: 20,
      filters: { email: '', plan: '', status: '' },
      sortBy: 'createdAt',
      sortDir: 'desc',
      dialogVisible: false,
      editForm: {},
      planOptions: PLAN_OPTIONS,
      testUploadEnabled: false,
      testUploadMaxSizeMb: 20480,
    };
  },
  computed: {
    sortProp() {
      return this.sortBy;
    },
    sortOrder() {
      return this.sortDir === 'asc' ? 'ascending' : 'descending';
    },
  },
  mounted() {
    this.load();
    this.loadTestUploadConfig();
  },
  methods: {
    async loadTestUploadConfig() {
      try {
        const data = await fetchAdminConfig();
        this.testUploadEnabled = !!data.testUploadEnabled;
        this.testUploadMaxSizeMb = data.testUploadMaxSizeMb || 20480;
      } catch (e) {
        // ignore
      }
    },
    planLabel,
    formatDateTime,
    async load() {
      this.loading = true;
      try {
        const data = await fetchUsers({
          page: this.page,
          size: this.size,
          email: this.filters.email || undefined,
          plan: this.filters.plan || undefined,
          status: this.filters.status || undefined,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
        });
        this.items = data.items || [];
        this.total = data.total || 0;
      } catch (e) {
        this.$message.error(e.message || '加载失败');
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 0;
      this.load();
    },
    onReset() {
      this.filters = { email: '', plan: '', status: '' };
      this.sortBy = 'createdAt';
      this.sortDir = 'desc';
      this.page = 0;
      this.load();
    },
    onPageChange(p) {
      this.page = p - 1;
      this.load();
    },
    onSortChange({ prop, order }) {
      if (!prop || !order) return;
      this.sortBy = prop;
      this.sortDir = order === 'ascending' ? 'asc' : 'desc';
      this.load();
    },
    openEdit(row) {
      this.editForm = {
        userId: row.userId,
        email: row.email,
        plan: row.plan,
        status: row.status,
        planExpiresAt: row.planExpiresAt || null,
        overrideMaxFileMb: row.overrideMaxFileMb || undefined,
      };
      this.dialogVisible = true;
    },
    async save() {
      try {
        const payload = {
          plan: this.editForm.plan,
          status: this.editForm.status,
          planExpiresAt: this.editForm.planExpiresAt || null,
        };
        if (this.testUploadEnabled) {
          payload.overrideMaxFileMb = this.editForm.overrideMaxFileMb || null;
        }
        await updateUser(this.editForm.userId, payload);
        this.$message.success('已保存');
        this.dialogVisible = false;
        this.load();
      } catch (e) {
        this.$message.error(e.message || '保存失败');
      }
    },
  },
};
</script>

<style scoped>
.filter-form { margin-bottom: 12px; }
.pager { margin-top: 16px; text-align: right; }
.form-hint { margin-top: 6px; font-size: 12px; color: #6b7280; line-height: 1.5; }
.unit-suffix { margin-left: 8px; font-size: 13px; color: #6b7280; }
</style>
